import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Save, RotateCcw, Zap, AlertCircle, CheckCircle2, Upload, FileText, Loader2, X, TrendingUp } from 'lucide-react';
import parserDonneesBrutesTURPE, { parserENEDISMultiPDL, detecterPDLsMultiples } from '../utils/parserDonneesBrutesTURPE';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MOIS_LABELS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const AdminReactifTURPE = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Test avec fichier
  const [testFile, setTestFile] = useState(null);
  const [testDomaine, setTestDomaine] = useState('BT > 36 kVA');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testInfo, setTestInfo] = useState(null); // {nbPoints, pasMin, pdlsDetectes}

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get('/admin/reactif-config');
      setConfig(data);
    } catch (e) {
      toast.error('Erreur chargement configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/reactif-config', config);
      toast.success('Configuration sauvegardée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser aux valeurs ENEDIS par défaut ?')) return;
    try {
      const { data } = await api.post('/admin/reactif-config/reset');
      setConfig(data);
      toast.success('Configuration réinitialisée');
    } catch (e) {
      toast.error('Erreur réinitialisation');
    }
  };

  const updatePrice = (section, domain, value) => {
    setConfig({
      ...config,
      [section]: {
        ...config[section],
        prix_kvarh: { ...config[section].prix_kvarh, [domain]: parseFloat(value) || 0 }
      }
    });
  };

  const toggleMonth = (section, month) => {
    const current = config[section].mois_factures || [];
    const newList = current.includes(month)
      ? current.filter(m => m !== month)
      : [...current, month].sort((a, b) => a - b);
    setConfig({
      ...config,
      [section]: { ...config[section], mois_factures: newList }
    });
  };

  // Test avec fichier
  const handleTestFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTestFile(file);
    setTestResult(null);
    setTestInfo(null);
  };

  const runTest = async () => {
    if (!testFile) {
      toast.error('Sélectionnez un fichier');
      return;
    }
    setTestRunning(true);
    try {
      const text = await testFile.text();
      // Détecter si multi-PDL
      const pdls = detecterPDLsMultiples(text);
      let parsed;
      if (pdls.length > 1) {
        const multi = parserENEDISMultiPDL(text);
        // Prendre le premier PDL pour le test
        const firstPdl = pdls[0];
        parsed = multi[firstPdl];
      } else {
        parsed = parserDonneesBrutesTURPE(text, pdls[0] || 'TEST');
      }
      if (!parsed || !parsed.tousLesPoints || parsed.tousLesPoints.length === 0) {
        toast.error('Aucune donnée exploitable dans ce fichier');
        return;
      }
      if (!parsed.hasInductiveCapacitive) {
        toast.error('Ce fichier ne contient pas de colonnes PRI et PRC séparées (format ENEDIS R63 requis)');
        return;
      }
      const samples = parsed.tousLesPoints
        .filter(p => p.kVAr_inductif != null || p.kVAr_capacitif != null)
        .map(p => ({
          timestamp: new Date(p.horodate).toISOString(),
          p_w: (p.kW || 0) * 1000,
          pri_var: (p.kVAr_inductif || 0) * 1000,
          prc_var: (p.kVAr_capacitif || 0) * 1000,
        }));
      setTestInfo({
        nbPoints: samples.length,
        pasMin: parsed.pasTempsMinutes,
        pdlsDetectes: pdls.length,
        firstPdl: pdls[0] || 'TEST',
      });
      const { data } = await api.post('/turpe/reactif/analyze', {
        samples,
        step_minutes: parsed.pasTempsMinutes || 5,
        domaine_tension: testDomaine,
        config_override: config,  // utilise la config en cours, non sauvegardée
      });
      setTestResult(data);
      toast.success('Analyse terminée');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || e.message || 'Erreur lors du test');
    } finally {
      setTestRunning(false);
    }
  };

  const clearTest = () => {
    setTestFile(null);
    setTestResult(null);
    setTestInfo(null);
  };

  if (loading) return <div className="py-12 text-center text-[#687175]">Chargement…</div>;
  if (!config) return null;

  return (
    <div data-testid="admin-reactif-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-[#191D1E] flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#F9C80E]" />
            Config. Énergie Réactive TURPE 7
          </h1>
          <p className="text-[#687175] text-sm mt-1">
            Seuils, prix et périodes de facturation de l'énergie réactive inductive/capacitive
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-[#BFC6CC] text-[#687175] rounded-lg hover:bg-[#F5F6F9] text-sm"
            data-testid="reset-config-btn">
            <RotateCcw className="w-4 h-4" /> Réinitialiser défaut ENEDIS
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-[#10D183] hover:bg-[#0BB873] text-white px-5 py-2 rounded-lg font-medium disabled:opacity-50"
            data-testid="save-config-btn">
            <Save className="w-4 h-4" /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Règles TURPE 7 (août 2025)</p>
          <p>
            <strong>Inductif</strong> : facturé si tan φ &gt; seuil en heures pleines hiver (nov-mars, 6h-22h).
            Formule : kVArh facturés = kVArh − (seuil × kWh). <br/>
            <strong>Capacitif</strong> : facturé si tan φ_cap &gt; seuil max en saison basse (avr-oct) pour HTA.
          </p>
        </div>
      </div>

      {/* Inductif */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6 mb-6" data-testid="section-inductif">
        <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#397DFF]"></span>
          Énergie Réactive Inductive (soutirée)
        </h2>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-1">
              Seuil tan φ (au-delà : facturation)
            </label>
            <input type="number" step="0.05" min="0" max="2"
              value={config.inductif.tan_phi_seuil}
              onChange={(e) => setConfig({ ...config, inductif: { ...config.inductif, tan_phi_seuil: parseFloat(e.target.value) || 0 } })}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm"
              data-testid="inductif-tan-phi-seuil" />
            <p className="text-xs text-[#687175] mt-1">Défaut ENEDIS : 0,4</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-1">Période de facturation (mois)</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {[...Array(12)].map((_, i) => {
                const m = i + 1;
                const active = config.inductif.mois_factures?.includes(m);
                return (
                  <button key={m} onClick={() => toggleMonth('inductif', m)}
                    className={`px-2 py-1 text-xs rounded ${active ? 'bg-[#397DFF] text-white' : 'bg-[#F5F6F9] text-[#687175] border border-[#BFC6CC]'}`}
                    data-testid={`inductif-month-${m}`}>
                    {MOIS_LABELS[m].substring(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#191D1E] mb-2">Prix (€/kVArh) par domaine de tension</label>
          <div className="grid grid-cols-3 gap-3">
            {['BT_SUP_36', 'HTA', 'HTB'].map(k => (
              <div key={k}>
                <div className="text-xs text-[#687175] mb-1">{k === 'BT_SUP_36' ? 'BT > 36 kVA (C4)' : k} (C1-C3)</div>
                <input type="number" step="0.0001" min="0"
                  value={config.inductif.prix_kvarh?.[k] ?? 0}
                  onChange={(e) => updatePrice('inductif', k, e.target.value)}
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm font-mono"
                  data-testid={`inductif-prix-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Capacitif */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6 mb-6" data-testid="section-capacitif">
        <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#F9C80E]"></span>
          Énergie Réactive Capacitive (injectée)
        </h2>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-1">
              Seuil tan φ max (au-delà : facturation)
            </label>
            <input type="number" step="0.05" min="0" max="2"
              value={config.capacitif.tan_phi_max}
              onChange={(e) => setConfig({ ...config, capacitif: { ...config.capacitif, tan_phi_max: parseFloat(e.target.value) || 0 } })}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm"
              data-testid="capacitif-tan-phi-max" />
            <p className="text-xs text-[#687175] mt-1">Défaut ENEDIS : 0,60</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-1">Période de facturation (mois)</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {[...Array(12)].map((_, i) => {
                const m = i + 1;
                const active = config.capacitif.mois_factures?.includes(m);
                return (
                  <button key={m} onClick={() => toggleMonth('capacitif', m)}
                    className={`px-2 py-1 text-xs rounded ${active ? 'bg-[#F9C80E] text-[#191D1E]' : 'bg-[#F5F6F9] text-[#687175] border border-[#BFC6CC]'}`}
                    data-testid={`capacitif-month-${m}`}>
                    {MOIS_LABELS[m].substring(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#191D1E] mb-2">Prix (€/kVArh) par domaine de tension</label>
          <div className="grid grid-cols-3 gap-3">
            {['BT_SUP_36', 'HTA', 'HTB'].map(k => (
              <div key={k}>
                <div className="text-xs text-[#687175] mb-1">{k === 'BT_SUP_36' ? 'BT > 36 kVA (C4)' : k}</div>
                <input type="number" step="0.0001" min="0"
                  value={config.capacitif.prix_kvarh?.[k] ?? 0}
                  onChange={(e) => updatePrice('capacitif', k, e.target.value)}
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm font-mono"
                  data-testid={`capacitif-prix-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dimensionnement */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-[#191D1E] mb-3">Dimensionnement recommandation</h2>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-[#191D1E] mb-1">
            Coefficient de dimensionnement batterie
          </label>
          <input type="number" step="0.05" min="0.1" max="2"
            value={config.dimensionnement_coef}
            onChange={(e) => setConfig({ ...config, dimensionnement_coef: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm"
            data-testid="dim-coef" />
          <p className="text-xs text-[#687175] mt-1">
            Taille batterie recommandée = coef × P99 (kVAR). Défaut : 0,85.
          </p>
        </div>
      </div>

      {/* Test avec fichier */}
      <div className="bg-white border-2 border-dashed border-[#397DFF] rounded-lg p-6 mb-6" data-testid="test-file-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-[#191D1E] flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#397DFF]" />
              Tester avec mon fichier
            </h2>
            <p className="text-sm text-[#687175] mt-1">
              Glissez un fichier CDC ENEDIS pour valider les paramètres en cours <strong>(non sauvegardés)</strong> sur un cas réel
            </p>
          </div>
          {testResult && (
            <button onClick={clearTest} className="text-[#687175] hover:text-[#191D1E]" data-testid="clear-test-btn">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {!testResult && (
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Fichier CSV ENEDIS</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleTestFile}
                className="w-full text-sm border border-[#BFC6CC] rounded-lg p-2 file:mr-3 file:bg-[#397DFF] file:text-white file:border-0 file:rounded file:px-3 file:py-1.5"
                data-testid="test-file-input"
              />
              {testFile && (
                <div className="mt-2 flex items-center gap-2 text-sm text-[#687175]">
                  <FileText className="w-4 h-4" />
                  {testFile.name} ({(testFile.size / 1024).toFixed(0)} Ko)
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Domaine de tension</label>
              <select
                value={testDomaine}
                onChange={(e) => setTestDomaine(e.target.value)}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm"
                data-testid="test-domaine-select"
              >
                <option value="BT > 36 kVA">BT &gt; 36 kVA (C4)</option>
                <option value="HTA">HTA (C1-C3)</option>
                <option value="HTB">HTB</option>
              </select>
            </div>
            <div className="col-span-3">
              <button
                onClick={runTest}
                disabled={!testFile || testRunning}
                className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="run-test-btn"
              >
                {testRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                {testRunning ? 'Analyse…' : 'Lancer le test'}
              </button>
            </div>
          </div>
        )}

        {testResult && (
          <div data-testid="test-result">
            {/* Résumé fichier */}
            <div className="bg-[#F5F6F9] rounded-lg p-3 mb-4 flex flex-wrap gap-4 text-xs text-[#687175]">
              <span>Fichier : <strong>{testFile?.name}</strong></span>
              <span>Pas : <strong>{testInfo?.pasMin} min</strong></span>
              <span>Échantillons : <strong>{testInfo?.nbPoints?.toLocaleString('fr-FR')}</strong></span>
              <span>PDL : <strong>{testInfo?.firstPdl}</strong></span>
              <span>Domaine : <strong>{testDomaine}</strong></span>
              {testResult.from_override && <span className="text-orange-600"><strong>⚠ Test avec config NON sauvegardée</strong></span>}
            </div>

            {/* Cartes résumé */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-white rounded p-3 border border-[#BFC6CC]">
                <div className="text-xs text-[#687175]">Énergie active</div>
                <div className="font-bold text-[#191D1E]">{testResult.aggregate.total.kwh_active?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} kWh</div>
              </div>
              <div className="bg-white rounded p-3 border border-blue-200">
                <div className="text-xs text-[#687175]">Inductif</div>
                <div className="font-bold text-blue-700">{testResult.aggregate.total.kvarh_inductif?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} kVArh</div>
              </div>
              <div className="bg-white rounded p-3 border border-yellow-200">
                <div className="text-xs text-[#687175]">Capacitif</div>
                <div className="font-bold text-yellow-700">{testResult.aggregate.total.kvarh_capacitif?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} kVArh</div>
              </div>
              <div className="bg-white rounded p-3 border border-red-200">
                <div className="text-xs text-[#687175]">Pénalité totale</div>
                <div className="font-bold text-red-600" data-testid="test-penalty-total">{testResult.penalties.total.penalty_total_eur?.toFixed(2).replace('.', ',')} €/an</div>
              </div>
            </div>

            {/* Détail */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                <div className="text-xs uppercase text-blue-700 font-semibold mb-1">Pénalité Inductive</div>
                <div className="text-2xl font-bold text-blue-700">{testResult.penalties.total.penalty_inductif_eur?.toFixed(2).replace('.', ',')} €/an</div>
                <div className="text-xs text-[#687175] mt-1">
                  {testResult.penalties.total.kvarh_factures_inductif?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} kVArh facturés
                  · seuil tan φ = {testResult.config_used.seuil_inductif} · prix = {testResult.config_used.prix_inductif_eur_kvarh} €/kVArh
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                <div className="text-xs uppercase text-yellow-700 font-semibold mb-1">Pénalité Capacitive</div>
                <div className="text-2xl font-bold text-yellow-700">{testResult.penalties.total.penalty_capacitif_eur?.toFixed(2).replace('.', ',')} €/an</div>
                <div className="text-xs text-[#687175] mt-1">
                  {testResult.penalties.total.kvarh_factures_capacitif?.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} kVArh facturés
                  · seuil tan φ_max = {testResult.config_used.seuil_capacitif} · prix = {testResult.config_used.prix_capacitif_eur_kvarh} €/kVArh
                </div>
              </div>
            </div>

            {/* Graphique mensuel */}
            <div className="bg-white rounded p-3 border border-[#BFC6CC] mb-4">
              <div className="text-xs font-medium text-[#687175] uppercase mb-2">Énergie réactive par mois (kVArh)</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={Object.entries(testResult.penalties.monthly).sort().map(([key, m]) => ({
                  mois: key.split('-')[1] + '/' + key.split('-')[0].slice(2),
                  inductif: m.kvarh_inductif,
                  capacitif: m.kvarh_capacitif,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="inductif" stackId="a" fill="#397DFF" name="Inductif" />
                  <Bar dataKey="capacitif" stackId="a" fill="#F9C80E" name="Capacitif" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recommandation */}
            <div className={`p-3 rounded border-2 ${testResult.recommendation.type_equipement === 'AUCUN' ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-[#F9C80E]'}`} data-testid="test-recommendation">
              <div className="font-semibold text-[#191D1E]">{testResult.recommendation.titre}</div>
              <div className="text-sm text-[#687175] mt-1">{testResult.recommendation.description}</div>
            </div>

            <div className="mt-4 flex gap-3">
              <button onClick={clearTest} className="px-4 py-2 border border-[#BFC6CC] text-[#687175] rounded-lg text-sm hover:bg-[#F5F6F9]" data-testid="new-test-btn">
                Nouveau test
              </button>
              <button onClick={runTest} disabled={testRunning} className="px-4 py-2 bg-[#397DFF] text-white rounded-lg text-sm disabled:opacity-50" data-testid="rerun-test-btn">
                Re-tester avec la config en cours
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-sm text-[#687175] bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg p-4">
        <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10D183]" />
        <p>
          Ces paramètres s'appliquent à toutes les analyses TURPE contenant des données de puissance réactive (PRI/PRC
          ENEDIS). Le calcul se fait automatiquement à l'import du fichier Courbe de Charge.
        </p>
      </div>
    </div>
  );
};

export default AdminReactifTURPE;
